import StyleSheet from '../../utils/helpers/styleSheet'
import {Img} from '../../models/editor/img'
import {v4 as uuidv4} from 'uuid'
import {origo} from '../../utils/helpers/origo'
import {useAppDispatch, useAppSelector} from '../../stores/configureStore'
import {
  addImageHistoryItem,
  addImageSelected,
  setAttribute,
} from '../../stores/editor/editor.reducer'
import {PixaBayImage} from '../../models/toolbox/pixaBayImage'
import {useEffect, useState} from 'react'
import services from '../../utils/services'
import {
  selectToolboxTabPanel,
  setPixaBayKeyword,
} from '../../stores/toolbox/toolbox.reducer'
import AwesomeDebouncePromise from 'awesome-debounce-promise'
import {ImgMasked} from '../../models/editor/imgMasked'
import {IMG_ADDED} from '../../models/editor/imgHistoryItemAction'
import {useTranslation} from 'react-i18next'

const styles: StyleSheet = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchContainer: {
    borderBottom: '1px solid #707070',
    padding: '10px 20px',
    display: 'flex',
    flexDirection: 'row',
  },
  searchInput: {
    width: '100%',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
  },
  loaderContainer: {
    height: '100%',
  },
  imagesContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, calc(50% - 0.5px))',
    backgroundColor: '#707070',
    gridGap: 1,
  },
  imageWrapper: {
    padding: 5,
    backgroundColor: 'white',
  },
  image: {
    height: 0,
    paddingBottom: '66%',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    cursor: 'pointer',
  },
  loader: {
    background: 'url(/polotervezo/loading.gif) no-repeat center/80px',
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
  },
}

export default function PixaBay() {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {pixaBayKeyword, selectForMasked} = useAppSelector(
    store => store.toolbox,
  )
  const {printArea} = useAppSelector(store => store.editor)
  const [images, setImages] = useState<PixaBayImage[]>([])
  const [loading, setLoading] = useState(false)
  const selectForImage = useAppSelector(store =>
    store.editor.images.find(img => img.id === selectForMasked?.id),
  ) as ImgMasked | undefined

  const trySearchImages = async (keyword: string) => {
    try {
      const response = await services.searchImages(keyword)
      const responseData = response.data
      setImages(() =>
        responseData.hits.map(item => ({
          id: item.id,
          smallImageUrl: item.webformatURL,
          largeImageUrl: item.largeImageURL,
          tags: item.tags,
        })),
      )
    } catch (error) {
      setImages(() => [])
    }
  }
  const [debouncedSearch] = useState(() =>
    AwesomeDebouncePromise(trySearchImages, 1000),
  )

  useEffect(() => {
    ;(async () => {
      setLoading(() => true)
      await debouncedSearch(pixaBayKeyword)
      setLoading(() => false)
    })()
  }, [debouncedSearch, pixaBayKeyword])

  const selectImage = async (item: PixaBayImage) => {
    if (selectForMasked && selectForImage) {
      const newInside = [...selectForImage.inside]
      newInside[selectForMasked.insideIndex] = {
        ...newInside[selectForMasked.insideIndex],
        url: item.largeImageUrl,
        position: origo,
        scale: 1,
      }
      dispatch(
        setAttribute({
          id: selectForMasked.id,
          data: {inside: newInside},
        }),
      )
      dispatch(
        selectToolboxTabPanel({
          tabPanel: 'Edit',
          selectForMasked: selectForMasked,
        }),
      )
    } else {
      const newImg: Img = {
        id: uuidv4(),
        type: 'url',
        url: item.largeImageUrl,
        position: printArea?.position ?? origo,
        scale: 1,
        rotation: 0,
        brightness: 1,
        mirroredX: false,
        mirroredY: false,
        inert: false,
        locked: false,
        filters: {
          inverse: false,
          monochrome: false,
          sepia: false,
        },
      }
      dispatch(addImageSelected({image: newImg}))
      dispatch(addImageHistoryItem({item: {img: newImg, action: IMG_ADDED}}))
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.searchContainer}>
        <input
          style={styles.searchInput}
          className="select"
          type="text"
          value={pixaBayKeyword}
          placeholder={t('Keresés...')}
          onChange={e => {
            dispatch(setPixaBayKeyword({keyword: e.target.value}))
          }}></input>
      </div>
      <div style={styles.loaderContainer}>
        <div style={styles.imagesContainer}>
          {images.map(item => (
            <div style={styles.imageWrapper} key={`toolboxItem_${item.id}`}>
              <div
                style={{
                  ...styles.image,
                  backgroundImage: `url(${item.smallImageUrl})`,
                }}
                title={item.tags}
                onClick={() => {
                  selectImage(item)
                }}></div>
            </div>
          ))}
        </div>
        <div style={loading ? styles.loader : {display: 'none'}}></div>
      </div>
    </div>
  )
}
